// Lógica pura de los paneles laterales de Columnas y Filtros de la hoja de obras.
// Sin React: búsqueda sin tildes, agrupación, recuentos por valor y rangos de fecha.
import { EMPTY_FILTER_VALUE, filterValueLabel } from "../obraFilter";

// Texto comparable: sin tildes, minúsculas y espacios colapsados.
export function normalizeQuery(value) {
    return String(value ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/\s+/g, " ")
        .trim();
}

// Todas las palabras de la búsqueda deben aparecer (en cualquier orden) en alguno de los textos.
export function matchesQuery(query, ...texts) {
    const words = normalizeQuery(query).split(" ").filter(Boolean);
    if (!words.length) return true;
    const haystack = texts.map(normalizeQuery).join(" ");
    return words.every((word) => haystack.includes(word));
}

// Trocea un texto para resaltar la primera coincidencia de cada palabra buscada.
// Devuelve [{ text, match }]; conserva tildes y mayúsculas del original.
export function highlightParts(text, query) {
    const source = String(text ?? "");
    const words = normalizeQuery(query).split(" ").filter(Boolean);
    if (!words.length || !source) return [{ text: source, match: false }];
    // normalizeQuery por carácter conserva la longitud (NFD + quitar diacríticos carácter a carácter).
    const folded = Array.from(source).map((char) => char.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().slice(0, 1) || char).join("");
    const marks = new Array(source.length).fill(false);
    words.forEach((word) => {
        const index = folded.indexOf(word);
        if (index >= 0) for (let offset = 0; offset < word.length; offset += 1) marks[index + offset] = true;
    });
    const parts = [];
    Array.from(source).forEach((char, index) => {
        const last = parts[parts.length - 1];
        if (last && last.match === marks[index]) last.text += char;
        else parts.push({ text: char, match: marks[index] });
    });
    return parts;
}

// Columnas: filtra grupos por texto y por visibilidad ("all" | "visible" | "hidden").
// Las columnas fijas (locked) cuentan como visibles. Se quitan los grupos vacíos.
export function filterColumnGroups(groups, { query = "", show = "all", isVisible = () => false } = {}) {
    return groups
        .map((group) => ({
            ...group,
            columns: group.columns.filter((column) => {
                const visible = column.locked || isVisible(column.id);
                if (show === "visible" && !visible) return false;
                if (show === "hidden" && visible) return false;
                return matchesQuery(query, column.label, group.label);
            }),
        }))
        .filter((group) => group.columns.length);
}

// Estado de un grupo de columnas para su interruptor de cabecera.
export function groupVisibility(group, isVisible) {
    const toggleable = group.columns.filter((column) => !column.locked);
    const shown = toggleable.filter((column) => isVisible(column.id)).length;
    return {
        shown: shown + group.columns.filter((column) => column.locked).length,
        total: group.columns.length,
        toggleableIds: toggleable.map((column) => column.id),
        all: toggleable.length > 0 && shown === toggleable.length,
        none: shown === 0,
    };
}

// Campos de filtro: agrupa por groupLabel en el orden indicado; los grupos que no
// estén en el orden van al final. Filtra por texto (nombre del campo o del grupo).
export function groupFilterFields(fields, { query = "", order = [] } = {}) {
    const byGroup = new Map();
    fields.forEach((field) => {
        if (!matchesQuery(query, field.label, field.groupLabel)) return;
        const key = field.groupLabel || "Otros";
        if (!byGroup.has(key)) byGroup.set(key, []);
        byGroup.get(key).push(field);
    });
    const rank = (label) => {
        const index = order.indexOf(label);
        return index === -1 ? order.length : index;
    };
    return [...byGroup.entries()]
        .sort((a, b) => rank(a[0]) - rank(b[0]))
        .map(([label, groupFields]) => ({ label, fields: groupFields }));
}

// ¿Cumple la obra los filtros personalizados? filters = { category, number, date };
// fieldSets = { category: [...], number: [...], date: [...] } con { id, getValue }.
// Categoría: el valor debe estar entre los elegidos. Número y fecha: rango con
// extremos opcionales; en fecha, una obra sin fecha no pasa un rango activo.
export function matchesCustomFilterSet(obra, filters = {}, fieldSets = {}) {
    for (const field of fieldSets.category || []) {
        const selected = filters.category?.[field.id];
        if (selected && selected.length && !selected.includes(field.getValue(obra))) return false;
    }
    for (const field of fieldSets.number || []) {
        const range = filters.number?.[field.id];
        if (!range) continue;
        const value = field.getValue(obra);
        if ((range.min ?? "") !== "" && value < Number(range.min)) return false;
        if ((range.max ?? "") !== "" && value > Number(range.max)) return false;
    }
    for (const field of fieldSets.date || []) {
        const range = filters.date?.[field.id];
        if (!range) continue;
        const value = field.getValue(obra);
        if (!value) return false;
        if ((range.min ?? "") !== "" && value < range.min) return false;
        if ((range.max ?? "") !== "" && value > range.max) return false;
    }
    return true;
}

// Recuento de obras por valor de un campo categórico. Las obras sin dato (null o "")
// cuentan juntas en la opción «No informado» (EMPTY_FILTER_VALUE).
export function countByValue(obras, getValue) {
    const counts = new Map();
    obras.forEach((obra) => {
        const raw = getValue(obra);
        const value = raw == null || raw === "" ? EMPTY_FILTER_VALUE : raw;
        counts.set(value, (counts.get(value) || 0) + 1);
    });
    return counts;
}

// Filtros de categoría con los valores elegidos de un campo ya fijados (lista de valores,
// «Elegir todos», «Elegir coincidencias» o un clic en una vista de análisis). Conserva
// «No informado» (""): antes se descartaba y «Elegir todos» lo desmarcaba sin avisar. Sin
// valores, quita el filtro del campo.
export function withCategoryValues(category = {}, fieldId, values) {
    const next = { ...category };
    const clean = (values || []).filter((value) => value != null);
    if (clean.length) next[fieldId] = clean;
    else delete next[fieldId];
    return next;
}

// Opciones de un filtro categórico para la lista: filtradas por texto, primero las
// seleccionadas y luego por recuento (desc) y nombre. Las opciones sin obras con el
// resto de filtros (count 0) se mantienen, al final, para poder desmarcarlas o elegirlas.
export function buildValueOptions(values, { selected = [], counts = new Map(), query = "" } = {}) {
    const selectedSet = new Set(selected);
    const all = Array.from(new Set([...values, ...selected]));
    return all
        .filter((value) => selectedSet.has(value) || matchesQuery(query, filterValueLabel(value)))
        .map((value) => ({ value, label: filterValueLabel(value), selected: selectedSet.has(value), count: counts.get(value) || 0 }))
        .sort((a, b) => {
            if (a.selected !== b.selected) return a.selected ? -1 : 1;
            if ((a.count > 0) !== (b.count > 0)) return a.count > 0 ? -1 : 1;
            return String(a.value).localeCompare(String(b.value), "es", { numeric: true, sensitivity: "base" });
        });
}

const pad = (number) => String(number).padStart(2, "0");
const isoOf = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// Atajos de rango para filtros de fecha, relativos a hoy (fecha local, formato ISO).
export const DATE_PRESETS = [
    { id: "last30", label: "Últimos 30 días" },
    { id: "thisMonth", label: "Este mes" },
    { id: "next90", label: "Próximos 90 días" },
    { id: "thisYear", label: "Este año" },
];

export function datePresetRange(presetId, today = new Date()) {
    const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const shift = (days) => new Date(base.getFullYear(), base.getMonth(), base.getDate() + days);
    switch (presetId) {
        case "last30": return { min: isoOf(shift(-30)), max: isoOf(base) };
        case "thisMonth": return { min: isoOf(new Date(base.getFullYear(), base.getMonth(), 1)), max: isoOf(new Date(base.getFullYear(), base.getMonth() + 1, 0)) };
        case "next90": return { min: isoOf(base), max: isoOf(shift(90)) };
        case "thisYear": return { min: `${base.getFullYear()}-01-01`, max: `${base.getFullYear()}-12-31` };
        default: return null;
    }
}

// ¿Qué atajo coincide exactamente con el rango actual? (para marcarlo activo)
export function activeDatePreset(range, today = new Date()) {
    if (!range) return null;
    const match = DATE_PRESETS.find((preset) => {
        const candidate = datePresetRange(preset.id, today);
        return candidate.min === (range.min ?? "") && candidate.max === (range.max ?? "");
    });
    return match ? match.id : null;
}

const numberFormat = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 });

// Valor de un extremo de rango para mostrar: fecha "1 ene 2026", número "1.500.000".
export function formatBound(type, value) {
    if (value == null || value === "") return "";
    if (type === "date") {
        const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
        if (!match) return String(value);
        const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
        return date.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" }).replace(/\./g, "");
    }
    const number = Number(value);
    return Number.isFinite(number) ? numberFormat.format(number) : String(value);
}

// Resumen corto de un filtro (tarjeta del panel y chip de la hoja). "" si no tiene valor.
export function summarizeFilter(type, { selected = [], range = null } = {}) {
    if (type === "category") {
        if (!selected.length) return "";
        const labels = selected.map(filterValueLabel);
        return labels.length <= 2 ? labels.join(", ") : `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`;
    }
    const min = formatBound(type, range?.min);
    const max = formatBound(type, range?.max);
    if (!min && !max) return "";
    if (min && max) return `${min} – ${max}`;
    return min ? `desde ${min}` : `hasta ${max}`;
}
